import * as Sentry from '@sentry/browser';
import axios, { TransitionalOptions } from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayOfYear from 'dayjs/plugin/dayOfYear.js';
// It's important to import Days.js plugins with js extension, otherwise this happens https://dev.azure.com/prismacode/prismacloud/_workitems/edit/40999/
import duration from 'dayjs/plugin/duration.js';
import isBetween from 'dayjs/plugin/isBetween.js';
import localeData from 'dayjs/plugin/localeData.js';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import minMax from 'dayjs/plugin/minMax.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import utc from 'dayjs/plugin/utc.js';
import { createRoot } from 'react-dom/client';

import { LicenseInfo } from '@mui/x-license';

import App from '@app/App';
// Call it before any action
import '@app/browserValidator';
import initConfiguration from '@app/store';
import { authAxiosInterceptor } from '@shared/api/axios-extensions/axios-auth-interceptor';
import { axiosErrorHandler } from '@shared/api/axios-extensions/axiosErrorHandler';
import { noContentInterceptor } from '@shared/api/no-content-interceptor';
import { noStatusInterceptor } from '@shared/api/no-status-interceptor';
import { MATOMO_CONFIG, MUI_LICENSE_KEY, SENTRY_CONFIG, STAGE_NAME } from '@shared/constants';
import { authDataChanged } from '@shared/redux/actions/auth-actions';
import '@shared/util/dateLocaleRegistrator';
import { integrateLocalization } from '@shared/util/i18n';
import { initMatomo } from '@shared/util/matomo/matomo';
import { getSessionId, sessionAxiosInterceptor } from '@shared/util/session-helper';
import { getStoredOrDefaultCulture } from '@shared/util/user';

LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

dayjs.extend(isBetween);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(dayOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(minMax);

if (SENTRY_CONFIG.IsEnabled) {
  Sentry.init({
    dsn: SENTRY_CONFIG.DSN,
    environment: STAGE_NAME,
    release: VERSION,
    beforeSend(event) {
      event.extra = { ...event.extra, sessionId: getSessionId() };
      return event;
    },
  });
}
if (MATOMO_CONFIG.IsEnabled) {
  initMatomo(MATOMO_CONFIG.Url, MATOMO_CONFIG.SiteId);
}

// Make sessionId visible to UI tests.
window.sessionId = getSessionId();

const configuration = initConfiguration();
const store = configuration.store;
// restore user authentication state.
store.dispatch(authDataChanged());

axios.defaults.transitional = {
  silentJSONParsing: false,
  forcedJSONParsing: true,
  clarifyTimeoutError: true,
} as TransitionalOptions;
axios.interceptors.request.use(authAxiosInterceptor());
axios.interceptors.request.use(sessionAxiosInterceptor());
// noStatusInterceptor is registered first, so that a response without a status code
// is rejected before it reaches the rest of the chain and is handled by axiosErrorHandler.
axios.interceptors.response.use(noStatusInterceptor);
axios.interceptors.response.use((response) => response, axiosErrorHandler);
axios.interceptors.response.use(noContentInterceptor);

const render = () => {
  const container = document.getElementById('root');
  const root = createRoot(container!);
  root.render(<App store={store} configuration={configuration} />);
};

integrateLocalization(VERSION).then(() => {
  const culture = getStoredOrDefaultCulture();
  dayjs.locale(culture.dayJSLocale);

  render();
});
